import { FUNERAL_SIDO, FUNERAL_SIGUNGU } from "goi_common";
import { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from "next";
import parlors_discount from "@/constants/parlors_discount_detail.json";
import ParlorRegionContainer from "@/containers/Parlor/ParlorRegionContainer/ParlorRegionContainer";
import { ParlorDetailProps } from "@/types/parlor";
import { replaceSiGunToBlank } from "@/utils/replaceSigunToBlank";
import { getParlorInfoByRegionCode } from "@/utils/getRegionParlorInfo";

import { skipDevBuildInGetStaticPaths } from "@/utils/skipDevBuildInGetStaticPaths";

export const getStaticPaths: GetStaticPaths = async () => {
  const skipBuildResult = skipDevBuildInGetStaticPaths() as GetStaticPathsResult;
  if (skipBuildResult) {
    return skipBuildResult;
  }

  const sidoList = Array.from(FUNERAL_SIDO.values());
  const sidoPath = sidoList.map((sido) => {
    return {
      params: {
        code: sido,
      },
    };
  });

  const sigunguPaths = Array.from(FUNERAL_SIDO)
    .map((sido) => {
      const sigungus = FUNERAL_SIGUNGU.get(sido[0]);
      if (sigungus) {
        return Array.from(sigungus.values()).map((sigungu) => {
          return {
            params: {
              code: `${sido[1]}-${sigungu}`,
            },
          };
        });
      }
    })
    .filter((i) => i != null);

  return {
    paths: sidoPath.concat(sigunguPaths?.flat() as Array<any>),
    fallback: false,
  };
};

function getByValue(map: Map<number, any>, searchValue: any) {
  for (const [key, value] of map.entries()) {
    if (value === searchValue) return key;
  }
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const code = params?.code as string;

  if (code.includes("-")) {
    const [sido, sigungu] = code.split("-");
    const sidoNumber = getByValue(FUNERAL_SIDO, sido);
    const sigunguNumber = getByValue(FUNERAL_SIGUNGU.get(sidoNumber as number) as any, sigungu);

    const { regionParlors, hasSigunguParlor } = getParlorInfoByRegionCode(
      sidoNumber as number,
      sigunguNumber as number,
    );

    const sortedRegionParlors = regionParlors.sort((p) =>
      parlors_discount.map((dp) => dp.parlor_id).includes(Number(p.id)) ? -1 : 1,
    );

    const 가족장 = Number(
      (
        sortedRegionParlors.reduce((prev, curr) => {
          return curr.expected_charge_sum["가족장"] + prev;
        }, 0) / regionParlors.length
      ).toFixed(0),
    ).toLocaleString();
    const 무빈소장 = Number(
      (
        sortedRegionParlors.reduce((prev, curr) => {
          return curr.expected_charge_sum["무빈소"] + prev;
        }, 0) / regionParlors.length
      ).toFixed(0),
    ).toLocaleString();
    const 일반장 = Number(
      (
        sortedRegionParlors.reduce((prev, curr) => {
          return curr.expected_charge_sum["일반장"] + prev;
        }, 0) / regionParlors.length
      ).toFixed(0),
    ).toLocaleString();

    const sigunguKeyword = replaceSiGunToBlank(sigungu);

    const description = `${sigunguKeyword}장례식장 상세 비용, 가장 저렴한 ${sigunguKeyword}장례식장 비용 정보, 주변 ${sigunguKeyword}장례식장과 비교하기. ${sigunguKeyword}장례식장 무빈소장례 비용:${
      무빈소장 || 980000
    }원, ${sigunguKeyword}장례식장 가족장 비용:${
      가족장 || 3700000
    }원, ${sigunguKeyword}장례식장 일반장 비용:${
      일반장 || 480000
    }원. 상조, 장례식장, 장지 비용 한번에 알아보기`;

    return {
      props: {
        regionParlors,
        hasSigunguParlor,
        sidoName: sido,
        sido: sidoNumber,
        sigunguName: sigungu,
        sigungu: sigunguNumber,
        isSigungu: true,
        pageDescription: description,
        pageKeywords: `${sido} ${sigunguKeyword} 장례식장 추천, ${sigunguKeyword} 장례식장 비용, ${sigunguKeyword} 장례식장 추천 평균 예상 비용, ${sigunguKeyword} 장례식장 무빈소장 추천 비용, ${sigunguKeyword} 장례식장 가족장 추천 비용, ${sigunguKeyword} 장례식장 일반장 추천 비용, ${sigunguKeyword} 장례식장 무빈소장 추천, ${sigunguKeyword} 장례식장 가족장 추천, ${sigunguKeyword} 장례식장 일반장 추천`,
        pageOgTitle: `${sigungu}장례식장 추천, 비용, 무빈소장례, 가족장, 일반장 비용 비교하기 - [장례의 모든 것, 고이장례연구소]`,
        pageOgDescription: description,
        pageOgImage:
          regionParlors[0]?.images[0]?.url || `${process.env.NEXT_PUBLIC_CDN_URL}/ogimage.png`,
      },
    };
  } else {
    const sidoNumber = getByValue(FUNERAL_SIDO, code);

    const { regionParlors, hasSigunguParlor } = getParlorInfoByRegionCode(sidoNumber as number);

    const sortedRegionParlors = regionParlors.sort((p) =>
      parlors_discount.map((dp) => dp.parlor_id).includes(Number(p.id)) ? -1 : 1,
    );

    const 가족장 = Number(
      (
        sortedRegionParlors.reduce((prev, curr) => {
          return curr.expected_charge_sum["가족장"] + prev;
        }, 0) / regionParlors.length
      ).toFixed(0),
    ).toLocaleString();
    const 무빈소장 = Number(
      (
        sortedRegionParlors.reduce((prev, curr) => {
          return curr.expected_charge_sum["무빈소"] + prev;
        }, 0) / regionParlors.length
      ).toFixed(0),
    ).toLocaleString();
    const 일반장 = Number(
      (
        sortedRegionParlors.reduce((prev, curr) => {
          return curr.expected_charge_sum["일반장"] + prev;
        }, 0) / regionParlors.length
      ).toFixed(0),
    ).toLocaleString();

    const description = `${code}장례식장 상세 비용, 가장 저렴한 ${code}장례식장 비용 정보, 주변 장례식장과 비교하기. ${code}장례식장 무빈소장례 비용:${
      무빈소장 || 980000
    }원, ${code}장례식장 가족장 비용:${가족장 || 3700000}원, ${code}장례식장 일반장 비용:${
      일반장 || 480000
    }원. 상조, 장례식장, 장지 비용 한번에 알아보기`;

    return {
      props: {
        regionParlors: sortedRegionParlors,
        hasSigunguParlor,
        sidoName: code,
        sido: getByValue(FUNERAL_SIDO, code),
        isSigungu: false,
        pageDescription: description,
        pageKeywords: `${code} 장례식장 추천, ${code}장례식장 비용, ${code}장례식장 평균 예상 비용, ${code}장례식장 무빈소장례, ${code}장례식장 가족장, ${code}장례식장 일반장, ${code}장례식장 무빈소장례 비용,  ${code}장례식장 가족장 비용,  ${code}장례식장 일반장 비용`,
        pageOgTitle: `${code}장례식장 추천, 비용, 무빈소장례, 가족장, 일반장 비용 비교하기 - [장례의 모든 것, 고이장례연구소]`,
        pageOgDescription: description,
        pageOgImage:
          regionParlors[0]?.images[0]?.url || `${process.env.NEXT_PUBLIC_CDN_URL}/ogimage.png`,
      },
    };
  }
};

interface ParlorRegionPageProps {
  regionParlors: Array<ParlorDetailProps>;
  hasSigunguParlor: boolean;
  sido: string;
  sidoName: string;
  sigungu?: string;
  sigunguName?: string;
  isSigungu: boolean;
}

function ParlorRegionPage(props: ParlorRegionPageProps) {
  const { sido, sidoName, sigungu, sigunguName, isSigungu, regionParlors, hasSigunguParlor } =
    props;

  return (
    <>
      <ParlorRegionContainer
        regionParlors={regionParlors}
        hasSigunguParlor={hasSigunguParlor}
        sido={sido}
        sidoName={sidoName}
        sigungu={sigungu}
        sigunguName={sigunguName}
        isSigungu={isSigungu}
      />
    </>
  );
}

export default ParlorRegionPage;
